import { usePathname } from 'next/navigation';
import { City } from './interface';
import { formateCityName } from './formateCityName';
import { useCityContext } from './CityContext';

export interface CityDetails {
  name: string;
  _id: string;
}

/**
 * Extract city information from the current URL path
 * @param pathname - The current pathname from usePathname()
 * @returns CityDetails object with name and _id
 */
export const getCityFromUrl = (pathname: string): CityDetails => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const citySegment = pathSegments[0];

  // Known non-city route prefixes
  const nonCityRoutes = ['bike-rentals', 'userProfile', 'blogs', 'about-us', 'contact-us', 'faq', 'studio', 'api', 'admin', 'private'];

  if (!citySegment || nonCityRoutes.includes(citySegment)) {
    return { name: '', _id: '' };
  }

  return {
    name: citySegment,
    _id: '', // Will be populated when we have the city data
  };
};

/**
 * Find city by formatted name from a list of cities
 * @param cityName - The formatted city name from URL
 * @param cities - Array of available cities
 * @returns CityDetails object with name and _id
 */
export const findCityByName = (cityName: string, cities: City[]): CityDetails => {
  const found = cities.find((city: City) => formateCityName(city.name) === cityName);
  
  if (found) {
    return {
      name: formateCityName(found.name),
      _id: found._id,
    };
  }
  
  return {
    name: cityName,
    _id: '',
  };
};

/**
 * Hook to get current city from URL using centralized city context
 * @returns CityDetails object with name and _id
 */
export const useCityFromUrl = (): CityDetails => {
  const pathname = usePathname();
  const cityFromUrl = getCityFromUrl(pathname);
  
  // Use the context hook directly
  const { cities, getCityByFormattedName } = useCityContext();
  
  // If we have a city name in URL and cities are loaded, try to find the city
  if (cityFromUrl.name && cities.length > 0) {
    const foundCity = getCityByFormattedName(cityFromUrl.name);
    if (foundCity) {
      const cityDetails: CityDetails = {
        name: cityFromUrl.name,
        _id: foundCity._id,
      };
      return cityDetails;
    }
  }
  
  // If we have a city name but no cities loaded yet, return the city name without ID
  if (cityFromUrl.name) {
    return cityFromUrl;
  }
  
  return cityFromUrl;
};

/**
 * Generate URL for a specific city
 * @param cityName - The formatted city name
 * @param path - The path after city (e.g., 'bike-rentals', 'bike-rentals/search')
 * @param searchParams - Optional search parameters
 * @returns Complete URL string
 */
export const generateCityUrl = (
  cityName: string,
  path: string = 'bike-rentals',
  searchParams?: URLSearchParams
): string => {
  const baseUrl = `/${cityName}/${path}`;
  
  if (searchParams && searchParams.toString()) {
    return `${baseUrl}?${searchParams.toString()}`;
  }
  
  return baseUrl;
};

/**
 * Check if the current URL has a valid city
 * @param pathname - The current pathname
 * @returns boolean indicating if a city is present in URL
 */
export const hasCityInUrl = (pathname: string): boolean => {
  const cityDetails = getCityFromUrl(pathname);
  return Boolean(cityDetails.name);
}; 